import { computed, onBeforeUnmount, ref, shallowRef } from 'vue';
import type { ComputedRef, Ref, ShallowRef } from 'vue';
// radash's `debounce().cancel()` permanently disables the function (every later call then fires
// synchronously), which `clear()` would trip on the first keystroke. lodash's `cancel()` only
// drops the pending invocation and leaves the debouncer reusable — the semantics we need here.
import debounce from 'lodash/debounce';

import { ErrorService } from '@/core/shared/errors/error.service';

type AddressPrediction = {
  id: string;
  value: string;
};

type FetchAddressPredictions<T extends AddressPrediction> = (address: string) => Promise<T[]>;

type UseAddressPredictionsOptions = {
  delay?: number;
};

type AddressPredictionListItem = {
  id: string;
  title: string;
};

type UseAddressPredictionsReturn<T extends AddressPrediction> = {
  clear: () => void;
  isLoading: Ref<boolean>;
  items: ShallowRef<T[]>;
  listItems: ComputedRef<AddressPredictionListItem[]>;
  reset: () => void;
  search: (value: unknown) => void;
  searchText: Ref<string>;
};

export function useAddressPredictions<T extends AddressPrediction>(
  fetchAddressPredictions: FetchAddressPredictions<T>,
  options: UseAddressPredictionsOptions = {},
): UseAddressPredictionsReturn<T> {
  const searchText = ref('');
  const isLoading = ref(false);
  const items = shallowRef<T[]>([]);

  const listItems = computed(() => items.value.map((item) => ({ title: item.value, id: item.id })));

  const populateAddressPredictions = debounce(async (address: string) => {
    isLoading.value = true;

    try {
      const predictions = await fetchAddressPredictions(address);

      // Latest-query-wins: a superseded request (the search text has since moved on,
      // or was cleared) must not clobber what the current query is showing. Cancellation
      // of the prior request is handled transparently by the persistence layer.
      if (searchText.value === address) {
        items.value = predictions;
      }
    } catch (error) {
      if (searchText.value === address) {
        items.value = [];
      }
      ErrorService.captureException(error);
    } finally {
      if (searchText.value === address) {
        isLoading.value = false;
      }
    }
  }, options.delay ?? 300);

  function clear() {
    populateAddressPredictions.cancel();
    items.value = [];
    isLoading.value = false;
  }

  function reset() {
    searchText.value = '';
    clear();
  }

  function search(value: unknown) {
    searchText.value = typeof value === 'string' ? value : '';
    clear();

    if (!value || typeof value !== 'string' || !value.trim()) {
      return;
    }

    isLoading.value = true;
    void populateAddressPredictions(value);
  }

  onBeforeUnmount(() => {
    clear();
  });

  return {
    clear,
    isLoading,
    items,
    listItems,
    reset,
    search,
    searchText,
  };
}
